// File: src/components/QuestionInput/useSpeechInput.ts

import { useCallback, useEffect, useRef, useState } from "react";
import { getBackendUri } from "../../api/backendUri";
import { getN8NSessionId } from "../../api/n8nAdapter";
import { SiteId } from "../../api/models";
import { isSpeechInputSupported, supportsBrowserSpeechRecognition } from "../../utils/speechSupport";
import { currentHostSource, resolveClientChannel } from "../../utils/clientChannel";
import { currentHostStore } from "../../utils/hostContext";
import { startAzureSpeechRecognition, AzureSpeechHandle, SpeechTokenResponse } from "./azureSpeechBackend";

const NOTICE_DURATION_MS = 4000;
// The "Preparing microphone…" notice only appears if resolving the backend (gateway round
// trip, and for Azure the SDK dynamic import) takes noticeably long — not on every tap.
const PREPARING_NOTICE_DELAY_MS = 300;

// ENG-019: "rate-limited" is GET /speech/token answering 429 (per-IP limit on the gateway) —
// a distinct notice from "network" so it gets its own, more specific message.
export type SpeechNotice = "permission" | "service" | "no-speech" | "no-device" | "network" | "rate-limited";

// ENG-012: one more per-attempt event than SpeechNotice covers — "ok" (successful recognition)
// and "aborted" (user- or code-initiated stop before a result) have no notice, and "sdk-load"
// (the Azure Speech SDK's dynamic import() failing) is reported as the "service" notice but
// tracked as its own usage outcome so it doesn't get confused with a real Azure service error.
export type SpeechUsageOutcome = SpeechNotice | "ok" | "aborted" | "sdk-load";

export type SpeechBackendKind = "browser" | "azure";

// The voice transcript currently sitting in the text field, if any (ENG-011 analytics: lets
// QuestionInput tell whether the message being sent came from voice and, if so, whether the
// user edited it afterwards). Persists across manual edits to the field so "sent text !=
// transcript" can be detected at send time — it is cleared only when the field is emptied
// (the clear button, backspacing to nothing) or a new mic recording starts.
// ENG-034: `backend` is a plain string (not SpeechBackendKind) because recordExternalInput()
// below also feeds this from ChatWidget.submitText({inputMode:"voice"}), whose backend id (e.g.
// "genie_native") is host-supplied, not one of this hook's own two recognizers.
export type LastVoiceInput = { source: "voice"; backend: string; transcript: string } | null;

// Maps Web Speech API error codes to a typed notice the UI can render a message for.
// "aborted" (user- or code-initiated stop) is intentionally left out: no notice for it.
const ERROR_NOTICE_MAP: Record<string, SpeechNotice | undefined> = {
    "not-allowed": "permission",
    "service-not-allowed": "service",
    "no-speech": "no-speech",
    "audio-capture": "no-device",
    network: "network"
};

type UseSpeechInputParams = {
    exampleLanguage: "el" | "en";
    setQuestion: (value: string) => void;
    disabled: boolean;
    textAreaRef: React.RefObject<HTMLTextAreaElement>;
    // ENG-012: identity/scoping for the /speech/usage event — same fields the chat request
    // already carries (see Chat.tsx makeApiRequest), read the same way where possible.
    siteId?: SiteId;
    isWidget?: boolean;
    // ENG-014: called once per attempt that ended in a successful, non-empty final result —
    // never on an error, no-speech, or a stop/abort before the recognizer finished on its own.
    // QuestionInput decides what to do with it (the auto-send grace period lives there).
    onFinal?: (text: string, backend: SpeechBackendKind) => void;
    // ENG-015: the admin kill switch for this client's channel (from GET /n8n-config). false
    // hides the mic exactly like an unsupported browser. Default true.
    speechEnabled?: boolean;
    // ENG-015: called when GET /speech/token answers 403 speech_disabled (the admin turned voice
    // off after this page loaded) — the mic is then hidden until reload.
    onDisabledByAdmin?: () => void;
    // ENG-054: the conversation thread on screen; the usage ping carries that thread's session.
    conversationThreadKey?: string;
};

type UseSpeechInputResult = {
    supported: boolean;
    listening: boolean;
    notice: SpeechNotice | null;
    // True once resolving+preparing the backend has taken > PREPARING_NOTICE_DELAY_MS.
    preparing: boolean;
    // ENG-011: the voice transcript behind the current text, if the field's content originated
    // from a mic tap. null once the field has been emptied or no voice was used this turn.
    lastInput: LastVoiceInput;
    // Clears lastInput — called by QuestionInput when the field is emptied by the user (typing
    // to "" or the clear button), so an unrelated later message isn't misreported as voice.
    clearLastInput: () => void;
    toggleListening: () => void;
    abortListening: () => void;
    // ENG-034: records a transcript this hook did not itself recognize — used for
    // ChatWidget.submitText({inputMode:"voice"}), so it gets the exact same voiceEdited/
    // speechBackend computation at send time as a mic result (QuestionInput.sendQuestion()).
    recordExternalInput: (backend: string, transcript: string) => void;
    // ENG-071: the level bars' value for this frame, read from requestAnimationFrame (never React
    // state). 0..1, or null when there is no level to show and the bars pulse instead.
    readLevel: () => number | null;
};

const getSpeechRecognitionCtor = (): (new () => SpeechRecognition) | undefined => {
    if (typeof window === "undefined") {
        return undefined;
    }
    return window.SpeechRecognition || window.webkitSpeechRecognition;
};

// A local override for testing without an admin-configured gateway backend (e.g. `npm run
// dev` with no gateway running). Only consulted when the gateway itself reports "auto" —
// the admin's explicit choice, carried in the gateway's `backend` field, always wins.
const getLocalBackendOverride = (): SpeechBackendKind | undefined => {
    const widgetOverride = typeof window !== "undefined" ? (window as any).PublicWidgetConfig?.speechBackend : undefined;
    if (widgetOverride === "browser" || widgetOverride === "azure") {
        return widgetOverride;
    }
    const envOverride = (import.meta.env.VITE_SPEECH_BACKEND as string | undefined)?.trim();
    if (envOverride === "browser" || envOverride === "azure") {
        return envOverride;
    }
    return undefined;
};

type BackendDecision = { kind: "browser" } | { kind: "azure"; token: SpeechTokenResponse } | { kind: "notice"; notice: SpeechNotice };

type SpeechUsagePayload = {
    backend: SpeechBackendKind;
    outcome: SpeechUsageOutcome;
    durationMs: number;
    transcriptLength: number;
    language: string;
    channel: string;
    siteId?: string;
    userId?: string;
    sessionId?: string;
    // ENG-050: the host's store (hostContext.storeId / storeName), widget mode only, omitted when absent.
    hostStoreId?: string;
    hostStoreName?: string;
};

// One event per mic attempt, best-effort: never blocks the UI, never retries, and never
// carries audio or transcript text (only its length). sendBeacon survives the page/tab
// closing right after a tap; fetch(keepalive) is the fallback where it's unavailable.
const postSpeechUsage = (payload: SpeechUsagePayload): void => {
    try {
        const url = `${getBackendUri()}/speech/usage`;
        const body = JSON.stringify(payload);
        // Not sendBeacon: it always sends credentials, and a cross-origin gateway that answers
        // CORS with "*" (the widget default) must reject that. fetch(keepalive) survives the
        // page closing just as well, and "omit" keeps the request credential-free.
        void fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
            credentials: "omit"
        }).catch(() => {
            // best-effort — a failed usage ping must never surface to the user
        });
    } catch (error) {
        console.warn("[speech] usage ping failed", error);
    }
};

export const useSpeechInput = ({
    exampleLanguage,
    setQuestion,
    disabled,
    textAreaRef,
    siteId,
    isWidget,
    onFinal,
    speechEnabled = true,
    onDisabledByAdmin,
    conversationThreadKey
}: UseSpeechInputParams): UseSpeechInputResult => {
    // ENG-015: set once GET /speech/token said 403 — hides the mic until the page reloads.
    const [disabledByAdmin, setDisabledByAdmin] = useState(false);
    // The mic *button* only needs a secure context and some way to capture audio (see
    // utils/speechSupport.ts — shared with the profile menu's auto-send toggle, ENG-014), and
    // the admin not having turned voice off for this channel (ENG-015).
    const supportsBrowserRecognition = supportsBrowserSpeechRecognition();
    const supported = isSpeechInputSupported() && speechEnabled && !disabledByAdmin;
    const lang = exampleLanguage === "el" ? "el-GR" : "en-US";

    const [listening, setListening] = useState(false);
    const [notice, setNotice] = useState<SpeechNotice | null>(null);
    const [preparing, setPreparing] = useState(false);
    const [lastInput, setLastInput] = useState<LastVoiceInput>(null);

    const clearLastInput = useCallback(() => {
        setLastInput(null);
    }, []);

    // ENG-034: same shape as a mic result (source: "voice"), but the backend/transcript came
    // from outside this hook (ChatWidget.submitText).
    const recordExternalInput = useCallback((backend: string, transcript: string) => {
        setLastInput({ source: "voice", backend, transcript });
    }, []);

    // ENG-014: latest onFinal (read at call time, so the callbacks below never hold a stale one),
    // and whether the current attempt was stopped by the user/code rather than ending on its own.
    const onFinalRef = useRef(onFinal);
    onFinalRef.current = onFinal;
    const onDisabledByAdminRef = useRef(onDisabledByAdmin);
    onDisabledByAdminRef.current = onDisabledByAdmin;
    const stoppedEarlyRef = useRef<boolean>(false);
    const reportFinal = useCallback((text: string, backend: SpeechBackendKind) => {
        if (stoppedEarlyRef.current || !text.trim()) {
            return;
        }
        onFinalRef.current?.(text, backend);
    }, []);

    // ENG-012: one /speech/usage ping per mic attempt. attemptStartedAtRef marks when the
    // current attempt began (reset on every tap); usageReportedRef guards against reporting
    // twice for the same attempt (Safari can fire both onerror and onend, ENG-007).
    const attemptStartedAtRef = useRef<number>(0);
    const usageReportedRef = useRef<boolean>(false);
    // ENG-054: read when the ping goes out, like the host store, so a thread switch during an
    // attempt reports the thread now on screen.
    const conversationThreadKeyRef = useRef(conversationThreadKey);
    conversationThreadKeyRef.current = conversationThreadKey;

    const reportUsage = useCallback(
        (backend: SpeechBackendKind, outcome: SpeechUsageOutcome, transcriptLength: number) => {
            if (usageReportedRef.current) {
                return;
            }
            usageReportedRef.current = true;
            const widgetCfg = typeof window !== "undefined" ? (window as any).PublicWidgetConfig : undefined;
            // ENG-050: read at tap time, like the host source — the host may call setHostContext after init.
            const hostStore = isWidget ? currentHostStore() : {};
            postSpeechUsage({
                backend,
                outcome,
                durationMs: Math.max(0, Date.now() - attemptStartedAtRef.current),
                transcriptLength,
                language: lang,
                channel: resolveClientChannel(isWidget, currentHostSource()),
                siteId,
                userId: widgetCfg?.userId,
                sessionId: getN8NSessionId(siteId, widgetCfg?.userId, conversationThreadKeyRef.current),
                ...(hostStore.storeId ? { hostStoreId: hostStore.storeId } : {}),
                ...(hostStore.storeName ? { hostStoreName: hostStore.storeName } : {})
            });
        },
        [lang, isWidget, siteId]
    );

    const recognitionRef = useRef<SpeechRecognition | null>(null);
    // Browser backend only — the Azure backend tracks its own and reports it via onEnd.
    const browserTranscriptLengthRef = useRef<number>(0);
    // ENG-014: the browser backend's latest text and whether this attempt hit onerror.
    const browserTextRef = useRef<string>("");
    const browserErroredRef = useRef<boolean>(false);
    const azureHandleRef = useRef<AzureSpeechHandle | null>(null);
    // ENG-071: an Azure attempt can still be starting (SDK import, permission prompt) when the field
    // unmounts, before it has a handle the cleanup below could abort; it is aborted as soon as it
    // starts, so the microphone it now holds is released.
    const unmountedRef = useRef<boolean>(false);
    const noticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const preparingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearNoticeTimeout = useCallback(() => {
        if (noticeTimeoutRef.current) {
            clearTimeout(noticeTimeoutRef.current);
            noticeTimeoutRef.current = null;
        }
    }, []);

    const showNotice = useCallback(
        (next: SpeechNotice) => {
            setNotice(next);
            clearNoticeTimeout();
            noticeTimeoutRef.current = setTimeout(() => {
                setNotice(null);
                noticeTimeoutRef.current = null;
            }, NOTICE_DURATION_MS);
        },
        [clearNoticeTimeout]
    );

    const clearPreparingTimeout = useCallback(() => {
        if (preparingTimeoutRef.current) {
            clearTimeout(preparingTimeoutRef.current);
            preparingTimeoutRef.current = null;
        }
    }, []);

    const beginPreparing = useCallback(() => {
        clearPreparingTimeout();
        preparingTimeoutRef.current = setTimeout(() => {
            setPreparing(true);
            preparingTimeoutRef.current = null;
        }, PREPARING_NOTICE_DELAY_MS);
    }, [clearPreparingTimeout]);

    const endPreparing = useCallback(() => {
        clearPreparingTimeout();
        setPreparing(false);
    }, [clearPreparingTimeout]);

    // Cleanup on unmount: abort any in-flight recognition and pending timers.
    useEffect(() => {
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            recognitionRef.current?.abort();
            azureHandleRef.current?.abort();
            clearNoticeTimeout();
            clearPreparingTimeout();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cleanup when the input becomes disabled while listening.
    useEffect(() => {
        if (disabled) {
            stoppedEarlyRef.current = true;
            recognitionRef.current?.abort();
            azureHandleRef.current?.abort();
        }
    }, [disabled]);

    // Decides which backend to use for this tap, always starting with GET /speech/token (the
    // gateway is the source of truth for `backend`, admin-controlled). See ENG-009 ticket for
    // the full precedence: gateway backend (if not "auto") -> local override (if not "auto",
    // testing only) -> auto (azure if a token was actually issued, else browser).
    // ENG-013: asked on every tap (no per-session memory any more) so admin changes — backend,
    // silence timeouts — apply on the very next tap. The gateway caches the Azure STS token for
    // 8 minutes, so this is one cheap gateway round trip, not an Azure call.
    const resolveBackend = useCallback(async (): Promise<BackendDecision> => {
        const pickBrowser = (): BackendDecision => {
            if (!supportsBrowserRecognition) {
                return { kind: "notice", notice: "service" };
            }
            return { kind: "browser" };
        };

        const pickAzure = (payload: SpeechTokenResponse): BackendDecision => {
            if (!payload.token) {
                return pickBrowser();
            }
            return { kind: "azure", token: payload };
        };

        let response: Response;
        try {
            const channel = resolveClientChannel(isWidget, currentHostSource());
            response = await fetch(`${getBackendUri()}/speech/token?channel=${encodeURIComponent(channel)}`);
        } catch (error) {
            console.warn("[speech] error", "token-fetch", error);
            return { kind: "notice", notice: "network" };
        }

        if (response.status === 404) {
            return pickBrowser();
        }

        if (response.status === 403) {
            // ENG-015: voice was turned off for this channel after the page loaded.
            console.warn("[speech] error", "speech-disabled");
            setDisabledByAdmin(true);
            onDisabledByAdminRef.current?.();
            return { kind: "notice", notice: "service" };
        }

        if (response.status === 429) {
            // ENG-019: the gateway's per-IP rate limit on GET /speech/token was hit.
            console.warn("[speech] error", "rate-limited");
            return { kind: "notice", notice: "rate-limited" };
        }

        if (!response.ok) {
            console.warn("[speech] error", "token-status", response.status);
            return { kind: "notice", notice: "network" };
        }

        let payload: SpeechTokenResponse;
        try {
            payload = await response.json();
        } catch (error) {
            console.warn("[speech] error", "token-parse", error);
            return { kind: "notice", notice: "network" };
        }

        if (payload.backend === "azure") {
            return pickAzure(payload);
        }
        if (payload.backend === "browser") {
            return pickBrowser();
        }

        // "auto": the gateway left the choice open. A local override (testing only) wins;
        // otherwise use Azure if the gateway actually issued a token, else fall back to browser.
        const override = getLocalBackendOverride();
        if (override === "browser") {
            return pickBrowser();
        }
        return pickAzure(payload);
    }, [supportsBrowserRecognition, isWidget]);

    const startBrowserBackend = useCallback(() => {
        const SpeechRecognitionCtor = getSpeechRecognitionCtor();
        if (!SpeechRecognitionCtor) {
            // Shouldn't happen: resolveBackend only picks "browser" when supportsBrowserRecognition.
            setListening(false);
            return;
        }

        const recognition = new SpeechRecognitionCtor();
        recognition.lang = lang;
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
            console.info(`[speech] start lang=${lang}`);
        };

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            let text = "";
            for (let i = 0; i < event.results.length; i++) {
                text += event.results[i][0].transcript;
            }
            setQuestion(text);
            setLastInput({ source: "voice", backend: "browser", transcript: text });
            browserTranscriptLengthRef.current = text.length;
            browserTextRef.current = text;
        };

        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
            console.warn("[speech] error", event.error, event.message);
            const mappedNotice = ERROR_NOTICE_MAP[event.error];
            if (mappedNotice) {
                showNotice(mappedNotice);
            }
            // "aborted": no mapped notice — user- or code-initiated stop, falls back to idle silently.
            // Safari does not always fire onend after onerror, so leave the listening state here too.
            browserErroredRef.current = true;
            setListening(false);
            recognitionRef.current = null;
            // ENG-012: event.error is "aborted" for a user-/code-initiated stop before any
            // notice; anything else unmapped (rare/future codes) is reported as "service".
            reportUsage("browser", mappedNotice || (event.error === "aborted" ? "aborted" : "service"), browserTranscriptLengthRef.current);
        };

        recognition.onend = () => {
            setListening(false);
            recognitionRef.current = null;
            textAreaRef.current?.focus();
            // ENG-012: only fires if onerror didn't already report this attempt (Safari can
            // fire both, ENG-007) — a plain onend with no prior error means success.
            reportUsage("browser", "ok", browserTranscriptLengthRef.current);
            if (!browserErroredRef.current) {
                reportFinal(browserTextRef.current, "browser");
            }
        };

        recognitionRef.current = recognition;
        browserTranscriptLengthRef.current = 0;
        browserTextRef.current = "";
        browserErroredRef.current = false;
        recognition.start();
    }, [lang, setQuestion, showNotice, textAreaRef, reportUsage, reportFinal]);

    const startAzureBackend = useCallback(
        async (token: SpeechTokenResponse) => {
            let finalText = "";
            const handle = await startAzureSpeechRecognition(token, {
                onInterim: text => {
                    setQuestion(text);
                    setLastInput({ source: "voice", backend: "azure", transcript: text });
                },
                onFinal: text => {
                    finalText = text;
                    setQuestion(text);
                    setLastInput({ source: "voice", backend: "azure", transcript: text });
                },
                onNotice: azureNotice => showNotice(azureNotice),
                onEnd: (outcome, transcriptLength) => {
                    setListening(false);
                    azureHandleRef.current = null;
                    textAreaRef.current?.focus();
                    reportUsage("azure", outcome, transcriptLength);
                    if (outcome === "ok") {
                        reportFinal(finalText, "azure");
                    }
                }
            });
            azureHandleRef.current = handle;
            if (unmountedRef.current) {
                handle?.abort();
            }
            if (!handle) {
                // startAzureSpeechRecognition already called onNotice + onEnd on failure.
                setListening(false);
            }
        },
        [setQuestion, showNotice, textAreaRef, reportUsage, reportFinal]
    );

    const toggleListening = useCallback(() => {
        if (!supported || disabled) {
            return;
        }

        if (listening) {
            // A user stop is "premature" for ENG-014: whatever was heard stays in the field,
            // but it is never auto-sent.
            stoppedEarlyRef.current = true;
            recognitionRef.current?.stop();
            azureHandleRef.current?.stop();
            return;
        }

        // The mic replaces whatever was typed — it does not append to it.
        setQuestion("");
        setLastInput(null);
        setListening(true);
        beginPreparing();
        // ENG-012: a fresh attempt — start its clock and allow it to report usage once.
        attemptStartedAtRef.current = Date.now();
        usageReportedRef.current = false;
        stoppedEarlyRef.current = false;

        void resolveBackend()
            .then(decision => {
                endPreparing();
                if (decision.kind === "notice") {
                    showNotice(decision.notice);
                    setListening(false);
                    // No backend was ever engaged (e.g. the gateway's /speech/token round trip
                    // failed) — "browser" is the closest neutral label the fixed backend field allows.
                    reportUsage("browser", decision.notice, 0);
                    return undefined;
                }
                if (decision.kind === "browser") {
                    startBrowserBackend();
                    return undefined;
                }
                return startAzureBackend(decision.token);
            })
            .catch(error => {
                endPreparing();
                console.warn("[speech] error", "resolve-backend", error);
                showNotice("network");
                setListening(false);
                reportUsage("browser", "network", 0);
            });
    }, [
        supported,
        disabled,
        listening,
        setQuestion,
        beginPreparing,
        endPreparing,
        resolveBackend,
        showNotice,
        startBrowserBackend,
        startAzureBackend,
        reportUsage
    ]);

    const abortListening = useCallback(() => {
        stoppedEarlyRef.current = true;
        recognitionRef.current?.abort();
        azureHandleRef.current?.abort();
    }, []);

    // ENG-071: only Azure has a level, from the stream it shares with the SDK. The browser backend
    // keeps its audio to itself and a second getUserMedia would break its recognition on Android,
    // so it stays null there (the bars pulse).
    const readLevel = useCallback((): number | null => azureHandleRef.current?.readLevel() ?? null, []);

    return { supported, listening, notice, preparing, lastInput, clearLastInput, toggleListening, abortListening, recordExternalInput, readLevel };
};
